import { useEffect, useState } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import { useAuth } from '../context/AuthContext';
import { calcBenchmarksByCategory } from '../utils/benchmark';

// Fetch last N completed tasks for benchmarking. 200 covers most accounts
// without Firestore read pressure — benchmarks update in real time as tasks complete.
const BENCHMARK_LIMIT = 200;

/**
 * Subscribes to the most recent completed tasks and computes best-run benchmarks
 * per task category.
 *
 * Admins get company-wide benchmarks. Production users get benchmarks scoped to
 * their own tasks — Firestore rules require `createdBy == auth.uid` for non-admin
 * reads, so the query must include that filter or it will be rejected.
 *
 * Returns a Map keyed by taskCategoryId (or "custom:<taskName>" for custom tasks).
 * Cost fields are hidden at the component level when rendering for production users.
 */
export function useBestRunBenchmarks({ enabled = true } = {}) {
  const { companyId, user, isAdmin } = useAuth();
  const [benchmarks, setBenchmarks] = useState(new Map());
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) return undefined;
    if (!companyId) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    // Production users can only read their own tasks per Firestore rules.
    // Add createdBy filter so the query is not rejected. Admins query company-wide.
    const constraints = [
      where('companyId', '==', companyId),
      ...(isAdmin ? [] : user?.uid ? [where('createdBy', '==', user.uid)] : []),
      where('status', '==', 'completed'),
      where('archived', '==', false),
      orderBy('submittedAt', 'desc'),
      limit(BENCHMARK_LIMIT),
    ];

    const q = query(collection(db, COLLECTIONS.PRODUCTION_TASKS), ...constraints);

    const unsub = onSnapshot(
      q,
      (snap) => {
        const tasks = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setBenchmarks(calcBenchmarksByCategory(tasks));
        setLoading(false);
      },
      (err) => {
        console.error('[useBestRunBenchmarks] Firestore error:', err);
        setLoading(false);
      }
    );

    return unsub;
  }, [companyId, user?.uid, isAdmin, enabled]);

  return { benchmarks, loading };
}
